import {Badge, Icon, Typography} from '@sollapay/ui/components';
import {formatDateLocalized} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import type {FC} from 'react';

export interface ClarificationBannerProps {
  round: number;
  note: string | null;
  reviewerName: string | null;
  requestedAt: string | null;
}

export const ClarificationBanner: FC<ClarificationBannerProps> = ({
  round,
  note,
  reviewerName,
  requestedAt
}) => {
  const {t, i18n} = useTranslation('trustAccounts');
  const attribution = [reviewerName, requestedAt && formatDateLocalized(requestedAt, i18n.language)]
    .filter(Boolean)
    .join(' · ');

  return (
    <section
      data-slot="clarification-banner"
      className="rounded-xl border border-border-warning bg-background-warning p-4"
    >
      <div data-slot="header" className="flex items-start gap-2.5">
        <Icon name="message-square" className="mt-0.5 size-[18px] shrink-0 text-text-warning" />
        <div data-slot="heading" className="min-w-0 flex-1">
          <Typography as="h3" weight="bold" className="whitespace-normal">
            {t('activation.clarification.title')}
          </Typography>
          <Typography as="p" size="sm" color="secondary" className="mt-0.5 whitespace-normal">
            {t('activation.clarification.subtitle')}
          </Typography>
        </div>
        <Badge variant="warning" size="sm" className="bg-background-surface">
          {t('activation.clarification.round', {round})}
        </Badge>
      </div>
      <div
        data-slot="note"
        className="mt-3.5 rounded-lg border border-border-warning bg-background-surface px-3.5 py-3"
      >
        <Typography as="p" className="whitespace-pre-line">
          {note ?? t('activation.clarification.fallbackNote')}
        </Typography>
        {attribution && (
          <Typography as="p" size="xs" color="tertiary" className="mt-2">
            {attribution}
          </Typography>
        )}
      </div>
    </section>
  );
};
